import React, { useState, useMemo, useEffect, useCallback, useContext, useRef } from 'react';
import { Link } from 'react-router-dom';
import chessslogo from '../images/chessslogo.png';
import mansalva from '../images/Mansalva/Mansalva-Regular.ttf';
import GameSummary from './GameSummary';
import { TAGS } from '../util/constants'; // Import TAGS to use the exact keys
import { getLandingGames } from '../util/api/featuredContent';
import { fetchGame } from '../util/api/game';
import { Chess } from 'chess.js';
import { Chessboard } from 'react-chessboard';
import BOARD_THEMES from '../components/WorkingChessLogic/BoardStyle';
import iPhone from "../images/iPhone.png";
import appStoreImage from '../images/AppleStore.png';
import googlePlayImage from '../images/GoogleStore.png';
import mockup1 from '../images/Landing/Mockup_1.png';
import mockup2 from '../images/Landing/Mockup_2.png';
import mockup3 from '../images/Landing/Mockup_3.png';
import { MobileContext } from '../context/MobileContext';
import { getFirestore, collection, addDoc, serverTimestamp } from 'firebase/firestore';

import Brilliant from '../images/StockfishIcons/Brilliance.png'
import Blunder from '../images/StockfishIcons/Blunder.png'
import Theory from '../images/StockfishIcons/Theory.png'
import Lichess from '../images/lichessgray.png'
import crownGold from '../images/CrownGold.png'
import Charlize from '../images/Reviews/Charlize.png'

// Remove Engine class and replace with simple AI functions
function minimaxRoot(depth, game, isMaximisingPlayer) {
  const newGameMoves = game.moves({ verbose: true });
  let bestMove = -9999;
  let bestMoveFound;

  newGameMoves.forEach(newGameMove => {
    game.move(newGameMove);
    const value = minimax(depth - 1, game, -10000, 10000, !isMaximisingPlayer);
    game.undo();
    if (value >= bestMove) {
      bestMove = value;
      bestMoveFound = newGameMove;
    }
  });
  return bestMoveFound;
}

function minimax(depth, game, alpha, beta, isMaximisingPlayer) {
  if (depth === 0) {
    return -evaluateBoard(game.board());
  }

  const newGameMoves = game.moves({ verbose: true });

  if (isMaximisingPlayer) {
    let bestMove = -9999;
    for (let i = 0; i < newGameMoves.length; i++) {
      game.move(newGameMoves[i]);
      bestMove = Math.max(bestMove, minimax(depth - 1, game, alpha, beta, !isMaximisingPlayer));
      game.undo();
      alpha = Math.max(alpha, bestMove);
      if (beta <= alpha) {
        return bestMove;
      }
    }
    return bestMove;
  } else {
    let bestMove = 9999;
    for (let i = 0; i < newGameMoves.length; i++) {
      game.move(newGameMoves[i]);
      bestMove = Math.min(bestMove, minimax(depth - 1, game, alpha, beta, !isMaximisingPlayer));
      game.undo();
      beta = Math.min(beta, bestMove);
      if (beta <= alpha) {
        return bestMove;
      }
    }
    return bestMove;
  }
}

// Simple piece values for evaluation
const pieceValues = {
  p: 10,
  n: 30,
  b: 30,
  r: 50,
  q: 90,
  k: 900
};

function evaluateBoard(board) {
  let totalEvaluation = 0;
  for (let i = 0; i < 8; i++) {
    for (let j = 0; j < 8; j++) {
      totalEvaluation = evaluatePiece(board[i][j], totalEvaluation);
    }
  }
  return totalEvaluation;
}

function evaluatePiece(piece, totalEvaluation) {
  if (piece === null) {
    return totalEvaluation;
  }

  const pieceValue = pieceValues[piece.type];
  if (piece.color === 'w') {
    return totalEvaluation + pieceValue;
  } else {
    return totalEvaluation - pieceValue;
  }
}

// Create an isolated component for the rotating FEN board to prevent parent re-renders
const RotatingFenBoard = React.memo(({ initialFen, fenList, rotationSpeed = 750 }) => {
  const [fen, setFen] = useState(initialFen);
  const [index, setIndex] = useState(0);
  
  // Handle the rotation in this component's scope
  useEffect(() => {
    const interval = setInterval(() => {
      setIndex(prevIndex => {
        const nextIndex = (prevIndex + 1) % fenList.length;
        setFen(fenList[nextIndex]);
        return nextIndex;
      });
    }, rotationSpeed);
    
    return () => clearInterval(interval);
  }, [fenList, rotationSpeed]);
  
  // Render the chessboard with current FEN
  return (
    <div style={{
      width: '300px',
      height: '300px',
      border: '5px solid black',
      borderRadius: '5px',
      overflow: 'hidden',
      boxShadow: '0 0 20px gold'
    }}>
      <Chessboard
        id="RotatingFENBoard"
        position={fen}
        boardWidth={300}
        customDarkSquareStyle={{ backgroundColor: 'transparent', boxShadow: 'inset 0 0 5px 2px rgba(0, 0, 0, 0.5)' }}
        customLightSquareStyle={{ backgroundColor: 'transparent', boxShadow: 'inset 0 0 5px 2px rgba(0, 0, 0, 0.5)' }}
        showNotation={false}
        animationDuration={0}
      />
    </div>
  );
});

const Landing = () => {
  // App Store URLs
  const APP_STORE_URL = "https://apps.apple.com/app/id6751127037";
  const PLAY_STORE_URL = "https://play.google.com/store/apps/details?id=com.chessthread.app";

  const [activeTab, setActiveTab] = React.useState('for Learning');
  const [currentGameIndex, setCurrentGameIndex] = React.useState(0);
  const [landingGames, setLandingGames] = React.useState([]);
  const [loadedGameData, setLoadedGameData] = React.useState([]);
  const [isLoadingGames, setIsLoadingGames] = React.useState(true);
  const [error, setError] = React.useState(null);
  
  // Add state for email capture form
  const [email, setEmail] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState(null); // null, 'success', or 'error'
  const [errorMessage, setErrorMessage] = useState('');
  const [cooldownTimer, setCooldownTimer] = useState(0);

  // Email validation function
  const validateEmail = (email) => {
    const re = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return re.test(email);
  };

  // Handle email submission
  const handleEmailSubmit = async (e) => {
    e.preventDefault();
    
    if (isSubmitting) {
      setSubmitStatus('error');
      setErrorMessage(`Please wait ${cooldownTimer} seconds before submitting again`);
      return;
    }

    if (!validateEmail(email)) {
      setSubmitStatus('error');
      setErrorMessage('Please enter a valid email address');
      return;
    }

    setIsSubmitting(true);
    setSubmitStatus(null);
    setErrorMessage('');

    try {
      const db = getFirestore();
      await addDoc(collection(db, 'waitlist_emails'), {
        email,
        timestamp: serverTimestamp()
      });
      setSubmitStatus('success');
      setEmail('');
      
      // Start cooldown timer
      let timeLeft = 30;
      setCooldownTimer(timeLeft);
      
      const timer = setInterval(() => {
        timeLeft--;
        setCooldownTimer(timeLeft);
        
        if (timeLeft <= 0) {
          clearInterval(timer);
          setIsSubmitting(false);
          setCooldownTimer(0);
        }
      }, 1000);
      
    } catch (err) {
      console.error('Error submitting email:', err);
      setSubmitStatus('error');
      setErrorMessage('Failed to submit email. Please try again.');
      setIsSubmitting(false);
    }
  };
  
  // Add ref to track if data has been fetched
  const dataFetchedRef = useRef(false);
  
  // Add render counter for debugging
  const renderCount = useRef(0);
  
  // Update state variables for chess
  const [gamePosition, setGamePosition] = useState('rnbqkbnr/pppppppp/8/8/8/8/PPPPPPPP/RNBQKBNR w KQkq - 0 1');
  const [difficultyLevel, setDifficultyLevel] = useState(2); // Replace stockfishLevel
  const [slideDirection, setSlideDirection] = React.useState('');
  const [isThinking, setIsThinking] = useState(false);
  
  // Keep sampleFens for the new component
  const sampleFens = [
    '8/1RRRRR2/3R3k/3R4/3R3K/3R4/3R4/8 w - - 0 1', // Starting position
    '8/1R3R2/1R3R1k/1RRRRR2/1R3R1K/1R3R2/1R3R2/8 w - - 0 1',
    '8/1RRRRR2/1R5k/1RRRR3/1R5K/1R6/1RRRRR2/8 w - - 0 1',

    '8/8/8/8/8/8/8/8',

    '8/1RRRRR2/1R3R1k/1R6/1R5K/1R3R2/1RRRRR2/8 w - - 0 1',
    '8/1R3R2/1R3R1k/1RRRRR2/1R3R1K/1R3R2/1R3R2/8 w - - 0 1',
    '8/1RRRRR2/1R5k/1RRRR3/1R5K/1R6/1RRRRR2/8 w - - 0 1',
    '8/1RRRRR2/1R5k/1RRRRR2/5R1K/5R2/1RRRRR2/8 w - - 0 1',
    '8/1RRRRR2/1R5k/1RRRRR2/5R1K/5R2/1RRRRR2/8 w - - 0 1',

    '8/8/8/8/8/8/8/8',

    '8/1RRRRR2/1R3R1k/1RRRRR2/1R1R3K/1R2R3/1R3R2/8 w - - 0 1',
    '8/1RRRRR2/1R5k/1RRRR3/1R5K/1R6/1RRRRR2/8 w - - 0 1',
    '8/1RRRRR2/1R3R1k/1RRRRR2/1R5K/1R6/1R6/8 w - - 0 1',
    '8/1R3R2/1R3R1k/1R3R2/1R3R1K/1R3R2/1RRRRR2/8 w - - 0 1',
    '8/1RRRRR2/1R3R1k/1RRRRR2/1R2R2K/1R3R2/1RRRRR2/8 w - - 0 1',
    '8/1R6/1R5k/1R6/1R5K/1R6/1RRRRR2/8 w - - 0 1',
    '8/1RRRRR2/3R3k/3R4/3R3K/3R4/1RRRRR2/8 w - - 0 1',
    '8/1RRRRR2/1R3R1k/1R6/1R5K/1R3R2/1RRRRR2/8 w - - 0 1',

    '8/8/8/8/8/8/8/8',
  ];
  
  // Add state for testimonial carousel
  const [currentTestimonialIndex, setCurrentTestimonialIndex] = useState(0);
  const [isTransitioning, setIsTransitioning] = useState(false);
  const [transitionDirection, setTransitionDirection] = useState('next');
  const [isCarouselHovered, setIsCarouselHovered] = useState(false);
  const [googlePlayClicked, setGooglePlayClicked] = useState(false);
  
  // Mockup switching state
  const mockups = [mockup1, mockup2, mockup3];
  const [currentMockupIndex, setCurrentMockupIndex] = useState(0);
  
  // Rotating words state (same as mobile)
  const rotatingWords = ['Creators', 'Players', 'Improvers', 'Coaches', 'Learners', 'Beginners', 'Masters'];
  const [currentWordIndex, setCurrentWordIndex] = useState(0);
  
  const isMobile = useContext(MobileContext);
  
  // Add a ref for the YouTube iframe
  const youtubeIframeRef = React.useRef(null);
  
  const game = useMemo(() => new Chess(), []);
  
  // Keep specific state tracking but with named conditions to help debugging
  React.useEffect(() => {
    
  }, [currentGameIndex]);
  
  React.useEffect(() => {
    
  }, [activeTab]);
  
  React.useEffect(() => {
    
  }, [loadedGameData]);
  
  // Get the first available tag key from TAGS object
  const availableTags = Object.keys(TAGS || {});
  const defaultTag = availableTags.length > 0 ? availableTags[0] : null;

  // Sample game summaries for the Players tab with data formatted for GameSummary component
  const gameSummaries = [
    {
      id: 1,
      creatorHandle: "MagnusC",
      caption: "Sicilian Defense Masterclass",
      tags: availableTags, // Use all available tags to ensure at least one matches
      moves: [
        { notation: "e4" },
        { notation: "c5" },
        { notation: "Nf3" },
        { notation: "d6" },
        { notation: "d4" },
        { notation: "cxd4" },
        { notation: "Nxd4" },
        { notation: "Nf6" }
      ],
      fen: 'rnbqkbnr/pppppppp/8/8/8/8/PPPPPPPP/RNBQKBNR w KQkq - 0 1'
    },
    {
      id: 2,
      creatorHandle: "Hikaru",
      caption: "Queen's Gambit Declined - Instructive Draw",
      tags: availableTags, // Use all available tags to ensure at least one matches
      moves: [
        { notation: "d4" },
        { notation: "d5" },
        { notation: "c4" },
        { notation: "e6" },
        { notation: "Nc3" },
        { notation: "Nf6" },
        { notation: "Bg5" },
        { notation: "Be7" }
      ],
      fen: 'rnbqkbnr/pppppppp/8/8/8/8/PPPPPPPP/RNBQKBNR w KQkq - 0 1'
    },
    {
      id: 3,
      creatorHandle: "Firouzja",
      caption: "Ruy Lopez - Modern Variation",
      tags: availableTags, // Use all available tags to ensure at least one matches
      moves: [
        { notation: "e4" },
        { notation: "e5" },
        { notation: "Nf3" },
        { notation: "Nc6" },
        { notation: "Bb5" },
        { notation: "a6" },
        { notation: "Ba4" },
        { notation: "Nf6" }
      ],
      fen: 'rnbqkbnr/pppppppp/8/8/8/8/PPPPPPPP/RNBQKBNR w KQkq - 0 1'
    }
  ];

  // Fetch landing games from Firestore and their data
  React.useEffect(() => {
    // Skip if data has already been fetched
    if (dataFetchedRef.current) return;
    dataFetchedRef.current = true;
    
    const fetchLandingGames = async () => {
      try {
        setIsLoadingGames(true);
        const gameIds = await getLandingGames();

        if (gameIds && gameIds.length > 0) {
          setLandingGames(gameIds);

          // Limit to 5 games maximum
          const gamesToFetch = gameIds.slice(0, 5);

          // Fetch the actual game data for all IDs at once
          const gameDataPromises = gamesToFetch.map(async (gameId) => {
            try {
              // Use the existing fetchGame function instead of direct fetch
              const game = await fetchGame(gameId);
              return {
                id: gameId,
                ...game,
                // Add any missing properties that GameSummary might expect
                // This ensures the component doesn't need to fetch anything else
                _fetchComplete: true  // Flag to indicate this data is complete
              };
            } catch (err) {
              console.error(`Error fetching game ${gameId}:`, err);
              return null;
            }
          });

          // Wait for all game data to be fetched
          const gameData = await Promise.all(gameDataPromises);

          // Filter out any null results (failed fetches)
          const validGameData = gameData.filter(game => game !== null);

          if (validGameData.length > 0) {
            setLoadedGameData(validGameData);
            setCurrentGameIndex(0);
          }
        } else {
          // If no games are configured, use the sample games
          
        }
        setIsLoadingGames(false);
      } catch (err) {
        console.error('Error fetching landing games:', err);
        setError('Failed to load games');
        setIsLoadingGames(false);
      }
    };

    fetchLandingGames();
  }, []);

  const nextGame = () => {
    setSlideDirection('left');
    if (loadedGameData.length > 0) {
      const nextIndex = currentGameIndex === loadedGameData.length - 1 ? 0 : currentGameIndex + 1;
      
      setCurrentGameIndex(nextIndex);
    } else if (landingGames.length > 0) {
      setCurrentGameIndex((prevIndex) =>
        prevIndex === landingGames.length - 1 ? 0 : prevIndex + 1
      );
    } else {
      setCurrentGameIndex((prevIndex) =>
        prevIndex === gameSummaries.length - 1 ? 0 : prevIndex + 1
      );
    }
    setTimeout(() => setSlideDirection(''), 500);
  };

  const prevGame = () => {
    setSlideDirection('right');
    if (loadedGameData.length > 0) {
      const prevIndex = currentGameIndex === 0 ? loadedGameData.length - 1 : currentGameIndex - 1;
      
      setCurrentGameIndex(prevIndex);
    } else if (landingGames.length > 0) {
      setCurrentGameIndex((prevIndex) =>
        prevIndex === 0 ? landingGames.length - 1 : prevIndex - 1
      );
    } else {
      setCurrentGameIndex((prevIndex) =>
        prevIndex === 0 ? gameSummaries.length - 1 : prevIndex - 1
      );
    }
    setTimeout(() => setSlideDirection(''), 500);
  };

  // Create a modified version of the GameSummary component that forces tag display
  const EnhancedGameSummary = (props) => {
    // Create a modified version of the game data with a guaranteed valid tag
    const enhancedGameData = {
      ...props.gameData,
      // Ensure the selectedTag is set directly if needed
      selectedTag: defaultTag
    };

    return <GameSummary {...props} gameData={enhancedGameData} />;
  };

  const bulletPointStyle = {
    color: 'white',
    fontSize: isMobile ? '3rem' : '1.1rem',
    lineHeight: '1.6',
    marginBottom: '12px',
    textAlign: 'left'
  };

  const bulletListStyle = {
    listStyleType: 'disc',
    paddingLeft: '20px',
    height: '90%',
    overflowY: 'auto',
    width: '90%',
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'space-between',
  };

  // Add button style for chess engine controls
  const buttonStyle = {
    padding: '10px 15px',
    margin: '5px',
    borderRadius: '5px',
    border: '1px solid #333',
    fontSize: '16px',
    cursor: 'pointer',
    fontWeight: 'bold',
    color: '#333',
  };

  // Replace findBestMove function with AI-based move
  function findBestMove() {
    setIsThinking(true);
    // Small delay to allow UI to update and show "thinking" state
    setTimeout(() => {
      try {
        const bestMove = minimaxRoot(difficultyLevel, game, true);
        if (bestMove) {
          game.move(bestMove);
          setGamePosition(game.fen());
        }
      } catch (err) {
        console.error("Error finding best move:", err);
      } finally {
        setIsThinking(false);
      }
    }, 100);
  }

  // Replace onDrop function
  function onDrop(sourceSquare, targetSquare) {
    if (isThinking) return false; // Prevent moves while AI is thinking

    try {
      const move = game.move({
        from: sourceSquare,
        to: targetSquare,
        promotion: 'q' // Always promote to queen for simplicity
      });

      // Update the position
      setGamePosition(game.fen());

      // Illegal move
      if (move === null) return false;

      // Exit if the game is over
      if (game.isGameOver() || game.isDraw()) return false;

      // Find engine's move
      setTimeout(findBestMove, 300);
      return true;
    } catch (err) {
      console.error("Error handling move:", err);
      return false;
    }
  }

  // Common styles for tab content
  const tabContainerStyle = isMobile
    ? {
        display: 'flex',
        flexDirection: 'column',
        gap: '30px',
        width: '100%',
        padding: '20px 0',
        backgroundColor: 'rgba(0,0,0,0.3)',
        borderRadius: '8px',
        minHeight: '2200px', // Add minimum height for mobile to prevent content overlap
        marginBottom: '250px', // Add margin at the bottom to push subsequent content down
      }
    : {
        display: 'grid',
        gridTemplateColumns: '1fr 1fr',
        gap: '30px',
        height: '600px', // Fixed height for all tabs
        width: '100%',
        padding: '20px',
        backgroundColor: 'rgba(0,0,0,0.3)',
        borderRadius: '8px'
      };

  const leftColumnStyle = isMobile
    ? {
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        width: '100%',
        marginBottom: '20px',
      }
    : {
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        height: '100%'
      };

  const rightColumnStyle = isMobile
    ? {
        color: 'white',
        fontSize: 'clamp(16px, 2vw, 18px)',
        overflowY: 'auto',
        padding: '20px 0',
        width: '100%',
        maxHeight: '2000px', // Add maximum height for the right column on mobile
      }
    : {
        color: 'white',
        fontSize: 'clamp(16px, 2vw, 18px)',
        overflowY: 'auto',
        padding: '20px',
        height: '100%',
      };

  // Sample testimonials - replace with your real testimonials
  // To update: simply add or modify entries in this array
  // Each testimonial needs: image (photo URL), name, credentials, and quote
  const testimonials = [
    {
      id: 1,
      image: Charlize, // Replace with actual testimonial images
      name: "Charlize van Zyl",
      credentials: "Woman International Master",
      quote: "This makes sharing chess effortless and fun."
    },
    {
      id: 2,
      image: chessslogo, // Replace with actual testimonial images
      name: "Michael R.",
      credentials: "FIDE Master, Chess Coach",
      quote: "I've tried many chess platforms, but this one offers the perfect balance of simplicity and powerful features for coaches."
    },

  ];

  // Memoize the nextTestimonial function to prevent unnecessary recreations
  const nextTestimonial = useCallback(() => {
    setTransitionDirection('next');
    setIsTransitioning(true);
    setTimeout(() => {
      setCurrentTestimonialIndex((prevIndex) => (prevIndex + 1) % testimonials.length);
      setIsTransitioning(false);
    }, 300);
  }, [testimonials.length]);

  // Function to navigate to the previous testimonial
  const prevTestimonial = useCallback(() => {
    setTransitionDirection('prev');
    setIsTransitioning(true);
    setTimeout(() => {
      setCurrentTestimonialIndex((prevIndex) =>
        prevIndex === 0 ? testimonials.length - 1 : prevIndex - 1
      );
      setIsTransitioning(false);
    }, 300);
  }, [testimonials.length]);

  // Auto-rotate testimonials every 5 seconds, but pause on hover
  useEffect(() => {
    let rotationInterval;

    if (!isCarouselHovered) {
      rotationInterval = setInterval(() => {
        nextTestimonial();
      }, 5000);
    }

    // Clean up interval on component unmount or when hover state changes
    return () => {
      if (rotationInterval) {
        clearInterval(rotationInterval);
      }
    };
  }, [isCarouselHovered, nextTestimonial]);

  // Auto-rotate mockups every 4 seconds
  useEffect(() => {
    const mockupInterval = setInterval(() => {
      setCurrentMockupIndex((prevIndex) => (prevIndex + 1) % mockups.length);
    }, 4000); // Change mockup every 4 seconds

    return () => clearInterval(mockupInterval);
  }, [mockups.length]);

  // Auto-rotate words every 2 seconds
  useEffect(() => {
    const wordInterval = setInterval(() => {
      setCurrentWordIndex((prevIndex) => (prevIndex + 1) % rotatingWords.length);
    }, 2000); // Change word every 2 seconds

    return () => clearInterval(wordInterval);
  }, [rotatingWords.length]);

  // Create memoized versions of the GameSummary components to prevent re-renders when other state changes
  const mainGameSummary = useMemo(() => {
    // Only calculate this when these specific dependencies change
    if (loadedGameData.length > 0) {
      return (
        <GameSummary
          gameData={loadedGameData[currentGameIndex]}
          boardWidth={350}
          maxContainerSize={500}
          preventAutoFetch={true}
          // onClick={() => {
          //   window.open(`/games/${loadedGameData[currentGameIndex].id}`, '_blank');
          // }}
        />
      );
    } else {
      return (
        <EnhancedGameSummary
          gameData={gameSummaries[currentGameIndex]}
          boardWidth={350}
          maxContainerSize={500}
          // onClick={() => {
          //   window.open("/portal", '_blank');
          // }}
        />
      );
    }
  }, [loadedGameData, currentGameIndex]); // Remove gameSummaries if it never changes

  const prevGameSummary = useMemo(() => {
    if (loadedGameData.length > 0) {
      const prevIndex = currentGameIndex === 0 ? loadedGameData.length - 1 : currentGameIndex - 1;
      return (
        <GameSummary
          gameData={loadedGameData[prevIndex]}
          boardWidth={325}
          maxContainerSize={500}
          preventAutoFetch={true}
        />
      );
    } else {
      const prevIndex = currentGameIndex === 0 ? gameSummaries.length - 1 : currentGameIndex - 1;
      return (
        <EnhancedGameSummary
          gameData={gameSummaries[prevIndex]}
          boardWidth={350}
          maxContainerSize={500}
        />
      );
    }
  }, [loadedGameData, currentGameIndex]); // Remove gameSummaries if it never changes

  const nextGameSummary = useMemo(() => {
    if (loadedGameData.length > 0) {
      const nextIndex = currentGameIndex === loadedGameData.length - 1 ? 0 : currentGameIndex + 1;
      return (
        <GameSummary
          gameData={loadedGameData[nextIndex]}
          boardWidth={325}
          maxContainerSize={500}
          preventAutoFetch={true}
        />
      );
    } else {
      const nextIndex = currentGameIndex === gameSummaries.length - 1 ? 0 : currentGameIndex + 1;
      return (
        <EnhancedGameSummary
          gameData={gameSummaries[nextIndex]}
          boardWidth={350}
          maxContainerSize={500}
        />
      );
    }
  }, [loadedGameData, currentGameIndex]); // Remove gameSummaries if it never changes

  // Tab Content
  const tabContent = {
    'for Learning': (
      <div style={tabContainerStyle}>
      
        <div style={leftColumnStyle}>
          {/* Game Summary carousel content */}
          <div style={{
            width: '100%',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            position: 'relative',
            height: '500px' // Fixed height to contain all summaries
          }}>
            {/* Left (Previous) Game Summary */}
            <div
              onClick={prevGame}
              style={{
                position: 'absolute',
                left: '-75px',
                transform: `scale(0.7)`,
                opacity: 0.5,
                cursor: 'pointer',
                transition: 'transform 0.5s cubic-bezier(0.4, 0, 0.2, 1)',
                zIndex: 1,
                filter: 'brightness(0.7)',
                ':hover': {
                  transform: 'scale(0.75)'
                }
              }}
            >
              {prevGameSummary}
            </div>
            


            {/* Main Active GameSummary */}
            <div style={{
              position: 'relative',
              zIndex: 2,
              transform: 'scale(1)',
              willChange: 'transform'
            }}
              className={`active-game-summary ${slideDirection ? `slide-${slideDirection}` : ''}`}
            >
              {mainGameSummary}
              {/* <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', fontSize: '14px', color: 'gray', marginTop: '10px' }}>(hover or click me)</div> */}

            </div>

            {/* Right (Next) Game Summary */}
            <div
              onClick={nextGame}
              style={{
                position: 'absolute',
                right: '-75px',
                transform: `scale(0.7)`,
                opacity: 0.5,
                cursor: 'pointer',
                transition: 'transform 0.5s cubic-bezier(0.4, 0, 0.2, 1)',
                zIndex: 1,
                filter: 'brightness(0.7)',
                ':hover': {
                  transform: 'scale(0.75)'
                }
              }}
            >
              {nextGameSummary}
            </div>
          </div>
        </div>
        <div style={rightColumnStyle}>
          <ul style={bulletListStyle}>
            <li style={bulletPointStyle}><span style={{ fontWeight: 'bold', color: 'gold' }}>Chess, But Social Media</span> – Scroll through interactive chess content, crafted by the community.</li>
            <li style={bulletPointStyle}><span style={{ fontWeight: 'bold', color: 'gold' }}>High-Impact Learning</span> – Quick, consise lessons that break down complex ideas.</li>
            <li style={bulletPointStyle}><span style={{ fontWeight: 'bold', color: 'gold' }}>Interactive Chessboards</span> – Follow the main line, or test out your own variations, all at your own pace.</li>
            <li style={bulletPointStyle}><span style={{ fontWeight: 'bold', color: 'gold' }}>Learn, Relearn, and Revisit</span> – Bookmark your favorite games to return to later.</li>
            <li style={bulletPointStyle}><span style={{ fontWeight: 'bold', color: '#57b047' }}>All This, And More</span> – Profiles, followers, notifications, sharing . . . Plenty to discover, and more to come. See for yourself, no credit card required.</li>

          </ul>
        </div>
      </div >
    ),

    'for Creating': (
      <div style={tabContainerStyle}>
        <div style={{ ...leftColumnStyle, gap: '30px' }}>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '30px', fontSize: '60px', marginTop: '0px' }}>
            <div style={{ display: 'flex', gap: '10px' }}>
              <img src={chessslogo} style={{ width: '70px', height: '70px' }}></img></div>
            <div style={{ display: 'flex', width: '70px', justifyContent: 'center', color: 'white' }}>
              <i className="fa-brands fa-youtube" onClick={() => window.open('https://www.youtube.com/@TheChessRepublicYT', '_blank')}></i>
            </div>
            <div style={{ display: 'flex', width: '70px', justifyContent: 'center', color: 'white' }}><i className="fa-brands fa-tiktok" onClick={() => window.open('https://www.tiktok.com/@thechessrepublic', '_blank')}></i>
            </div>
            <div style={{ display: 'flex', width: '70px', justifyContent: 'center', color: 'white' }}><i className="fa-brands fa-instagram" onClick={() => window.open('https://www.instagram.com/thechessrepublic/', '_blank')}></i>
            </div>
            <div style={{ display: 'flex', width: '70px', justifyContent: 'center', color: 'white' }}><i className="fa-brands fa-facebook" onClick={() => window.open('https://www.facebook.com/TheChessRepublic/', '_blank')}></i>
            </div>
            <div style={{ display: 'flex', width: '70px', justifyContent: 'center', color: 'white' }}><i className="fa-brands fa-x-twitter" onClick={() => window.open('https://x.com/ChessRepublicX', '_blank')}></i>


            </div>
          </div>

          {/* YouTube Short with iPhone overlay */}
          <div style={{
            width: '315px', // Container width
            height: '560px', // Container height
            position: 'relative', // Position relative for absolute children
            marginBottom: '20px',


          }}>
            {/* YouTube iframe */}
            <div style={{
              width: '272px', // Slightly narrower to fit inside iPhone screen
              height: '545px', // Slightly shorter to fit inside iPhone screen
              position: 'absolute',
              top: '10px', // Adjust to center in iPhone screen
              left: '20px', // Adjust to center in iPhone screen
              zIndex: 1, // Lower z-index to stay behind iPhone frame
              overflow: 'hidden',
              borderRadius: '25px', // Match iPhone screen corner radius
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
            }}>
              <iframe
                ref={youtubeIframeRef}
                width="100%" // Match parent width
                height="100%" // Match parent height
                title="Chess Creator Content"
                frameBorder="0"
                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                allowFullScreen
                style={{

                  width: '98%',
                  height: '98%'
                }}
              ></iframe>
            </div>

            {/* iPhone overlay */}
            <img
              src={iPhone}
              alt="iPhone frame"
              style={{
                position: 'absolute',
                top: 0,
                left: 0,
                width: '315px', // Match container width
                height: '560px', // Match container height
                zIndex: 2, // Higher z-index to overlay on top of video
                pointerEvents: 'none' // Allow clicks to pass through to video
              }}
            />

          </div>
        </div>
        <div style={rightColumnStyle}>
          <ul style={bulletListStyle}>
            <li style={bulletPointStyle}><span style={{ fontWeight: 'bold', color: 'gold' }}>Effortless Chess Content Creation</span> – Easily transform chess ideas into polished content.</li>
            <li style={bulletPointStyle}><span style={{ fontWeight: 'bold', color: 'gold' }}>Your Voice, Your Way</span> – Personalize your content with variations, arrows, annotations, and more.</li>
            <li style={bulletPointStyle}><span style={{ fontWeight: 'bold', color: 'gold' }}>Time-Saving Tools</span> – Slash editing time with intuitive automation designed for chess creators.</li>
            <li style={bulletPointStyle}><span style={{ fontWeight: 'bold', color: 'gold' }}>Any Game, Position, Puzzle, Lesson</span> – If it can live on a chessboard, it belongs on our platform.</li>
            <li style={bulletPointStyle}><span style={{ fontWeight: 'bold', color: '#57b047' }}>Find Your Audience, Build Your Brand</span> – Publish internally on our dynamic, chess-centric platform, then amplify your reach across major social media channels, using smart video automations.</li>
          </ul>
        </div>
      </div >
    ),

    'for Coaching': (
      <div style={tabContainerStyle}>
        <div style={leftColumnStyle}>
          {/* Replace static image with testimonial carousel */}
          <div
            style={{
              width: '400px',
              height: '500px',
              position: 'relative',
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center',
              overflow: 'hidden',
              borderRadius: '10px',
              backgroundColor: '#1a1a2e',
              border: '2px solid #3066be',
              boxShadow: '0 8px 16px rgba(0,0,0,0.4)',
              background: 'linear-gradient(to bottom, #1a1a2e, #0a0a16)',
            }}
            onMouseEnter={() => setIsCarouselHovered(true)}
            onMouseLeave={() => setIsCarouselHovered(false)}
          >
            {/* Navigation buttons */}
            <button
              onClick={prevTestimonial}
              style={{
                position: 'absolute',
                left: '10px',
                top: '50%',
                transform: 'translateY(-50%)',
                backgroundColor: 'rgba(0, 0, 0, 0.5)',
                color: 'white',
                border: 'none',
                borderRadius: '50%',
                width: '45px',
                height: '45px',
                fontSize: '20px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                cursor: 'pointer',
                zIndex: 2,
                transition: 'all 0.2s ease',
                boxShadow: '0 2px 5px rgba(0,0,0,0.3)',
              }}
              onMouseOver={(e) => {
                e.currentTarget.style.backgroundColor = 'rgba(0, 0, 0, 0.7)';
                e.currentTarget.style.color = 'gold';
                e.currentTarget.style.transform = 'translateY(-50%) scale(1.1)';
              }}
              onMouseOut={(e) => {
                e.currentTarget.style.backgroundColor = 'rgba(0, 0, 0, 0.5)';
                e.currentTarget.style.color = 'white';
                e.currentTarget.style.transform = 'translateY(-50%) scale(1)';
              }}
            >
              <i className="fa-solid fa-chevron-left"></i>
            </button>

            <button
              onClick={nextTestimonial}
              style={{
                position: 'absolute',
                right: '10px',
                top: '50%',
                transform: 'translateY(-50%)',
                backgroundColor: 'rgba(0, 0, 0, 0.5)',
                color: 'white',
                border: 'none',
                borderRadius: '50%',
                width: '45px',
                height: '45px',
                fontSize: '20px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                cursor: 'pointer',
                zIndex: 2,
                transition: 'all 0.2s ease',
                boxShadow: '0 2px 5px rgba(0,0,0,0.3)',
              }}
              onMouseOver={(e) => {
                e.currentTarget.style.backgroundColor = 'rgba(0, 0, 0, 0.7)';
                e.currentTarget.style.color = 'gold';
                e.currentTarget.style.transform = 'translateY(-50%) scale(1.1)';
              }}
              onMouseOut={(e) => {
                e.currentTarget.style.backgroundColor = 'rgba(0, 0, 0, 0.5)';
                e.currentTarget.style.color = 'white';
                e.currentTarget.style.transform = 'translateY(-50%) scale(1)';
              }}
            >
              <i className="fa-solid fa-chevron-right"></i>
            </button>

            {/* Testimonial content with transition */}
            <div
              style={{
                width: '100%',
                height: '100%',
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'center',
                padding: '20px',
                boxSizing: 'border-box',
                transition: 'opacity 0.3s ease, transform 0.3s ease',
                opacity: isTransitioning ? 0 : 1,
                transform: isTransitioning
                  ? `translateX(${transitionDirection === 'next' ? '-20px' : '20px'})`
                  : 'translateX(0)',
              }}
            >
              <div
                style={{
                  width: '200px',
                  height: '200px',
                  borderRadius: '50%',
                  overflow: 'hidden',
                  border: '3px solid gold',
                  marginBottom: '15px',
                  boxShadow: '0 4px 8px rgba(0,0,0,0.3)',
                }}
              >
                <img
                  src={testimonials[currentTestimonialIndex].image}
                  alt={testimonials[currentTestimonialIndex].name}
                  style={{
                    width: '100%',
                    height: '100%',
                    objectFit: 'cover',
                  }}
                />
              </div>

              <h3 style={{
                color: 'gold',
                margin: '0 0 5px 0',
                fontSize: '24px',
                textAlign: 'center',
                textShadow: '1px 1px 2px rgba(0,0,0,0.5)'
              }}>
                {testimonials[currentTestimonialIndex].name}
              </h3>

              <p style={{
                color: '#aaa',
                margin: '0 0 20px 0',
                fontSize: '16px',
                textAlign: 'center',
                fontWeight: 'bold'
              }}>
                {testimonials[currentTestimonialIndex].credentials}
              </p>

              <p style={{
                color: 'white',
                margin: 0,
                fontSize: '18px',
                textAlign: 'center',
                fontStyle: 'italic',
                lineHeight: '1.5',
                maxWidth: '90%',
                textShadow: '1px 1px 2px rgba(0,0,0,0.3)'
              }}>
                "{testimonials[currentTestimonialIndex].quote}"
              </p>
            </div>

            {/* Indicator dots */}
            <div style={{
              position: 'absolute',
              bottom: '15px',
              display: 'flex',
              gap: '8px',
            }}>
              {testimonials.map((_, index) => (
                <div
                  key={index}
                  onClick={() => {
                    setTransitionDirection(index > currentTestimonialIndex ? 'next' : 'prev');
                    setIsTransitioning(true);
                    setTimeout(() => {
                      setCurrentTestimonialIndex(index);
                      setIsTransitioning(false);
                    }, 300);
                  }}
                  style={{
                    width: currentTestimonialIndex === index ? '12px' : '10px',
                    height: currentTestimonialIndex === index ? '12px' : '10px',
                    borderRadius: '50%',
                    backgroundColor: currentTestimonialIndex === index ? 'gold' : 'rgba(255, 255, 255, 0.3)',
                    cursor: 'pointer',
                    transition: 'all 0.3s ease',
                    boxShadow: '0 2px 4px rgba(0,0,0,0.2)',
                    border: currentTestimonialIndex === index ? '1px solid rgba(255, 215, 0, 0.5)' : 'none'
                  }}
                />
              ))}
            </div>
          </div>
        </div>
        <div style={rightColumnStyle}>
          <ul style={bulletListStyle}>
            <li style={bulletPointStyle}><span style={{ fontWeight: 'bold', color: 'gold' }}>A Global Audience</span> – Reach ambitious players, eager to level up their chess skills. No borders, and no strings attached.</li>
            <li style={bulletPointStyle}><span style={{ fontWeight: 'bold', color: 'gold' }}>Streamline Your Coaching Funnel</span> – A seamless system for attracting, engaging, and retaining students to work with.</li>

            <li style={bulletPointStyle}><span style={{ fontWeight: 'bold', color: 'gold' }}>Zero Tech Stress</span> – We handle the technology, so you can focus on the coaching.</li>
            <li style={bulletPointStyle}><span style={{ fontWeight: 'bold', color: 'gold' }}>Hassle-Free Content Sharing</span> – Deliver lessons and analyses without the grind of video production.</li>
            <li style={bulletPointStyle}><span style={{ fontWeight: 'bold', color: '#57b047' }}>Build Your Chess Business</span> – More direct monetization, premium access, and sponsorship opportunities coming soon.</li>
          </ul>
        </div>
      </div>
    ),

    'for Playing': (
      <div style={tabContainerStyle}>
        <div style={leftColumnStyle}>
          <div style={{
            width: '400px',
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            cursor: 'pointer',
          }}
            onClick={() => window.open('https://lichess.org', '_blank')}>
            <img src={Lichess} style={{ width: '400px', height: '400px', objectFit: 'contain' }} />
            <div style={{ color: 'gray', fontSize: '40px', fontWeight: 'bold', marginBottom: '10px' }}>L i c h e s s . o r g</div>

            {/* <div style={{ color: 'gold', fontSize: '40px', fontWeight: 'bold', marginBottom: '10px' }}>Level 1 . . .</div> */}


            {/* <div style={{
              width: '400px',
              height: '400px',
              border: '5px solid black',
              borderRadius: '5px',
              overflow: 'hidden',
              position: 'relative'
            }}>
              <Chessboard
                id="PlayVsComputer"
                position={gamePosition}
                onPieceDrop={onDrop}
                boardWidth={400}
                customDarkSquareStyle={{ background: "linear-gradient(to left, #3066BE, #4A7DD9)" }}
                customLightSquareStyle={{ background: "linear-gradient(to left, #fffff0, #f5e4c3)" }}

              /> 
              
            </div> */}

            {/* <div style={{
              display: 'flex',
              justifyContent: 'center',
              marginTop: '15px',
              gap: '10px'
            }}>
              <button 
                                style={{ width: '100px', height: '50px', fontSize: '20px', fontWeight: 'bold', backgroundColor: 'gold', color: 'black', borderRadius: '5px', border: '5px solid black' }}

                onClick={() => {
                  if (game.history().length >= 2) {
                    game.undo();
                    game.undo();
                    setGamePosition(game.fen());
                  }
                }}
                disabled={isThinking || game.history().length < 2}
              >
                Undo
              </button>
              <button 
                style={{ width: '300px', height: '50px', fontSize: '20px', fontWeight: 'bold', backgroundColor: 'gold', color: 'black', borderRadius: '5px', border: '5px solid black' }}
                onClick={() => {
                  game.reset();
                  setGamePosition(game.fen());
                }}
                disabled={isThinking}
              >
                New Game
              </button>
              
            </div> */}
          </div>
        </div>
        <div style={rightColumnStyle}>
          <ul style={bulletListStyle}>
            <li style={bulletPointStyle}><span style={{ fontWeight: 'bold', color: 'gold' }}>Global Matchmaking On Lichess.org</span> – Connect instantly to millions of players worldwide and jump into PvP chess at any skill level.</li>
            <li style={bulletPointStyle}><span style={{ fontWeight: 'bold', color: 'gold' }}>Reliable And Seamless</span> – Experience fast, high-quality matches powered by Lichess' battle-tested infrastructure.</li>
            <li style={bulletPointStyle}><span style={{ fontWeight: 'bold', color: 'gold' }}>One Click To Analyze, Another To Share</span> – Elevate your game with FREE Stockfish review, just one click away after every match.
              <img src={Blunder} alt="Blunder" style={{ width: '25px', height: '25px', marginLeft: '10px' }} />
              <img src={Theory} alt="Theory" style={{ width: '25px', height: '25px', marginLeft: '10px' }} />
              <img src={Brilliant} alt="Brilliant" style={{ width: '25px', height: '25px', marginLeft: '10px' }} />
            </li>
            <li style={bulletPointStyle}><span style={{ fontWeight: 'bold', color: 'gold' }}>Free Forever</span> – Premium chess at no cost.</li>
            <li style={bulletPointStyle}><span style={{ fontWeight: 'bold', color: 'gold' }}>Credit Where It's Due</span> – We're thankful for Lichess' top-tier infrastructure, and will continutally donate to their organization for making this all possible.</li>
            <li style={bulletPointStyle}><span style={{ fontWeight: 'bold', color: 'gold' }}>Questions . . ?</span> – Visit our <span onClick={() => window.open('https://lichess.org', '_blank')} style={{ textDecoration: 'underline', cursor: 'pointer', color: 'gold' }} >FAQ page</span> to learn more about our LiChess integrations.</li>

          </ul>
        </div>
      </div>
    )
  };

  // Add useEffect to handle tab changes for YouTube video
  React.useEffect(() => {
    // If switching to the "for Creating" tab, set the YouTube source with autoplay
    if (activeTab === 'for Creating' && youtubeIframeRef.current) {
      // Set a small timeout to ensure the DOM is ready before setting the src
      setTimeout(() => {
        if (youtubeIframeRef.current) {
          // Add parameters to improve user experience:
          // autoplay=1 - start playing automatically
          // controls=0 - hide controls by default (still accessible via right-click)
          // showinfo=0 - hide video title and uploader info
          // rel=0 - prevent showing related videos
          // modestbranding=1 - reduce YouTube branding
          // iv_load_policy=3 - hide annotations
          // loop=1 - loop the video when it ends
          // playlist=VIDEO_ID - required for looping to work (must repeat the video ID)
          youtubeIframeRef.current.src = "https://www.youtube.com/embed/5bcT__RV384?autoplay=1&controls=0&showinfo=0&rel=0&modestbranding=1&iv_load_policy=3&fs=0&loop=1&playlist=5bcT__RV384";
        }
      }, 100);
    } else if (youtubeIframeRef.current) {
      // Reset the iframe source when switching away from the tab
      youtubeIframeRef.current.src = "";
    }
  }, [activeTab]);

  return (

    <div>
    {isMobile ? (
    <div className="" style={{
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      justifyContent: 'flex-start',
      background: "linear-gradient(to top, #3066BE, black)",
      width: '100%',
      minHeight: '100vh',
      margin: 0,
      padding: '20px 15px',
      overflow: 'auto',
      scrollbarWidth: 'thin',
      scrollbarColor: 'gold transparent',
      boxSizing: 'border-box'
    }}>

      <div style={{ height: '40px' }} /> {/* Spacer for extra top margin */}
      <img
        src={chessslogo}
        alt="ChessClip Logo"
        style={{ width: '150px', marginTop: '0px' }}
      />

      <style>
        {`
            @font-face {
              font-family: 'Mansalva';
              src: url(${mansalva}) format('truetype');
              font-weight: normal;
              font-style: normal;
            }

            @keyframes slideInFromRight {
              0% { 
                transform: scale(0.5) translateX(200px);
              }
              100% { 
                transform: scale(1) translateX(0);
              }
            }

            @keyframes slideInFromLeft {
              0% { 
                transform: scale(0.5) translateX(-200px);
              }
              100% { 
                transform: scale(1) translateX(0);
              }
            }

            .active-game-summary {
              animation: none;
            }

            .active-game-summary.slide-left {
              animation: slideInFromRight 0.5s cubic-bezier(0.4, 0, 0.2, 1) forwards;
            }

            .active-game-summary.slide-right {
              animation: slideInFromLeft 0.5s cubic-bezier(0.4, 0, 0.2, 1) forwards;
            }
          `}
      </style>
      <div style={{
        fontFamily: 'Mansalva, cursive',
        color: 'gold',
        fontSize: '2.2rem',
        marginBottom: '0px',
        marginTop: '20px',
      }}>ChessThread.com</div>
      {/* <div style={{
          width: '100%',
          textAlign: 'center',
          marginBottom: '60px',
          marginTop: '80px'
        }}>
          <div style={{
            width: '90%',
            margin: '0 auto',
            padding: '10px',
            backgroundColor: 'rgba(0,0,0,0.3)',
            borderRadius: '8px',
            border: '2px solid gold',
            boxShadow: '0 0 20px rgba(0,0,0,0.5)'
          }}>
            <h2 style={{
              color: 'gold',
              fontSize: '3rem',
              marginBottom: '20px',
              textShadow: '0 0 5px black'
            }}>
              Join The Waitlist
            </h2>
           
            <form onSubmit={handleEmailSubmit} style={{
              display: 'flex',
              flexDirection: 'column',
              gap: '15px',
              alignItems: 'center',
            }}>
              <input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="Enter your email"
                style={{
                  width: '600px',
                  marginBottom: '20px',
                  padding: '15px',
                  fontSize: '2rem',
                  borderRadius: '5px',
                  border: '2px solid gold',
                  backgroundColor: 'rgba(255,255,255,0.1)',
                  color: 'white',
                  outline: 'none'
                }}
                disabled={isSubmitting}
              />
              <button
                type="submit"
                disabled={isSubmitting}
                style={{
                  padding: '15px 0px',
                  fontSize: '2rem',
                  fontWeight: 'bold',
                  backgroundColor: 'gold',
                  marginBottom: '30px',
                  color: 'black',
                  border: 'none',
                  borderRadius: '5px',
                  cursor: isSubmitting ? 'not-allowed' : 'pointer',
                  opacity: isSubmitting ? 0.7 : 1,
                  transition: 'all 0.2s ease',
                  width: '400px'
                }}
              >
                {isSubmitting && cooldownTimer > 0 
                  ? `Please wait ${cooldownTimer}s...` 
                  : 'Submit'}
              </button>
              {submitStatus === 'error' && (
                <p style={{ color: '#ff4444', marginTop: '10px' }}>
                  {errorMessage}
                </p>
              )}
              {submitStatus === 'success' && (
                <p style={{ color: '#4CAF50', marginTop: '10px' }}>
                  Thank you! We'll be in touch soon.
                </p>
              )}
              {isSubmitting && cooldownTimer > 0 && (
                <p style={{ color: 'gold', marginTop: '10px', fontSize: '0.9rem' }}>
                  Form will be available in {cooldownTimer} seconds
                </p>
              )}
            </form>
          </div>
        </div> */}
      <div>
        {/* <div className="finally" style={{ position: 'relative', top: '75px', left: '-70px', display: 'inline-block', fontSize: '2rem', transform: 'rotate(-15deg)', textShadow: 'none', color: 'gold', textShadow: '0 0 5px black', fontStyle: 'italic', fontFamily: 'Mansalva' }}>Finally...</div> */}
        <div style={{
          fontSize: '1.3rem',
          color: 'white',
          marginBottom: '30px',
          lineHeight: '1.2',
          maxWidth: '100%',
          textShadow: '0 0 5px gold',
          textAlign: 'center',
          fontWeight: 'bold',
          marginTop: '30px',
          padding: '0 10px',
          boxSizing: 'border-box'
        }}>
          The Social Chess Platform<br />That You've Been Waiting For.
        </div>
        
        {/* Coming to Mobile Soon Message */}
        <div style={{
          fontSize: '1.2rem',
          color: 'gold',
          marginBottom: '30px',
          textAlign: 'center',
          fontWeight: 'bold',
          fontStyle: 'italic',
          backgroundColor: 'rgba(0,0,0,0.5)',
          padding: '15px 20px',
          borderRadius: '10px',
          border: '2px solid gold',
          maxWidth: '90%',
          width: '100%',
          margin: '20px auto 30px auto',
          boxSizing: 'border-box'
        }}>
          Coming to Mobile Soon!<br />
          <span style={{ fontSize: '1rem', color: 'white' }}>Try on Desktop</span>
        </div>
        
        {/* Bottom text matching desktop */}
        <div style={{
          fontSize: '1rem',
          color: 'gold',
          textAlign: 'center',
          fontWeight: 'bold',
          fontStyle: 'italic',
          marginTop: '30px',
          marginBottom: '20px',
          padding: '0 20px',
          maxWidth: '100%',
          lineHeight: '1.4',
          boxSizing: 'border-box'
        }}>
          Create chess content, engage with other's and make chess friends.
        </div>
        {/* <div style={{ fontSize: '2rem', color: 'white', textShadow: '0 0 5px black', fontStyle: 'italic', textAlign: 'center', margin: '50px 0px'}}>Coming To Desktop First...</div> */}
      </div>
      

      {/* Tabbed Content Section */}
      {/* <div style={{
        maxWidth: '1200px',
        margin: '30px 0',
        height: '700px', 
        display: 'flex',
        flexDirection: 'column',
        marginBottom: '1000px'
      }}>
       
        <div style={{
          display: 'flex',
          justifyContent: 'space-around',
          borderBottom: '3px solid gold',
          marginBottom: '15px'
        }}>
          {['for Learning', 'for Creating', 'for Coaching'].map(tab => (
            <button
              key={tab}
              onClick={() => {
                // When changing tabs, update the activeTab state
                setActiveTab(tab);
              }}
              style={{
                padding: '10px 20px',
                fontSize: '2.2rem',
                fontWeight: 'bold',
                background: activeTab === tab ? 'gold' : 'transparent',
                color: activeTab === tab ? 'black' : 'gold',
                border: 'none',
                borderRadius: '8px 8px 0 0',
                cursor: 'pointer',
                textTransform: 'capitalize'
              }}
            >
              {tab}
            </button>
          ))}
        </div>

        <div style={{
          padding: '15px',
          backgroundColor: 'rgba(0,0,0,0.3)',
          borderRadius: '0 0 8px 8px',
          flex: 1,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          overflow: 'visible' 
        }}>
          {typeof tabContent[activeTab] === 'string' ? (
            <p style={{
              fontSize: '1.4rem',
              lineHeight: '1.6',
              color: '#E0E0E0',
              textAlign: 'center'
            }}>
              {tabContent[activeTab]}
            </p>
          ) : (
            tabContent[activeTab]
          )}
        </div>
      </div> */}

      

      {/* Free Features Section */}
      {/* <div style={{
        maxWidth: '1135px',
        margin: '50px 0 30px',
        padding: '30px',
        backgroundColor: 'rgba(0,0,0,0.3)',
        borderRadius: '8px',
        border: '2px solid gold',
        color: 'white',
        boxShadow: '0 0 30px rgba(0,0,0,0.5)'
      }}>
        <div style={{
          fontSize: '3rem',
          color: 'gold',
          marginBottom: '15px',
          textAlign: 'center',
          fontWeight: 'bold'
        }}>
          Most of Our Features Are <span style={{ fontStyle: 'italic' }}>*Literally*</span> Free
        </div>

        <p style={{
          fontSize: '1.3rem',
          lineHeight: '1.6',
          marginBottom: '25px',
          textAlign: 'center',
          fontWeight: 'bold'
        }}>
          That's right - we give you free access to tools and experiences that other websites charge you for. <br /> Here's what you get, no strings attached:
        </p>

        <div style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fit, minmax(500px, 1fr))',
          gap: '30px',
          marginBottom: '30px'
        }}>
          <div style={{
            display: 'flex',
            flexDirection: 'column',
            gap: '15px'
          }}>
            <div style={{
              padding: '20px',
              backgroundColor: 'rgba(48, 102, 190, 0.3)',
              borderRadius: '8px',
              border: '1px solid #3066BE'
            }}>
              <h3 style={{
                fontSize: '1.6rem',
                color: 'gold',
                marginBottom: '10px'
              }}>
                Elite Stockfish Game Review Analysis
              </h3>
              <p style={{ fontSize: '1.1rem', lineHeight: '1.5' }}>
                Unlock unlimited reviews powered by the Stockfish engine—spot brilliancies, pinpoint mistakes, and elevate your chess skills, all at no cost.

                <img src={Blunder} style={{ width: '25px', height: '25px', marginLeft: '10px' }} />
                <img src={Theory} style={{ width: '25px', height: '25px', marginLeft: '10px' }} />
                <img src={Brilliant} style={{ width: '25px', height: '25px', marginLeft: '10px' }} />

              </p>
            </div>

            <div style={{
              padding: '20px',
              backgroundColor: 'rgba(48, 102, 190, 0.3)',
              borderRadius: '8px',
              border: '1px solid #3066BE'
            }}>
              <h3 style={{
                fontSize: '1.6rem',
                color: 'gold',
                marginBottom: '10px'
              }}>
                PvP Chess Against Millions
              </h3>
              <p style={{ fontSize: '1.1rem', lineHeight: '1.5' }}>
                Challenge players worldwide through Lichess.org with unlimited matches—top-tier matchmaking.
              </p>
            </div>
          </div>

          <div style={{
            display: 'flex',
            flexDirection: 'column',
            gap: '15px'
          }}>
            <div style={{
              padding: '20px',
              backgroundColor: 'rgba(48, 102, 190, 0.3)',
              borderRadius: '8px',
              border: '1px solid #3066BE'
            }}>
              <h3 style={{
                fontSize: '1.6rem',
                color: 'gold',
                marginBottom: '10px'
              }}>
                Endless Puzzles & Lessons
              </h3>
              <p style={{ fontSize: '1.1rem', lineHeight: '1.5' }}>
                Dive into unlimited community-created puzzles, lessons, and content to sharpen your game—free access, no paywalls.
              </p>
            </div>


            <div style={{
              padding: '20px',
              backgroundColor: 'rgba(48, 102, 190, 0.3)',
              borderRadius: '8px',
              border: '1px solid #3066BE'
            }}>
              <h3 style={{
                fontSize: '1.6rem',
                color: 'gold',
                marginBottom: '10px'
              }}>
                More on the Way
              </h3>
              <p style={{ fontSize: '1.1rem', lineHeight: '1.5' }}>
                Stay tuned for even greater features coming soon. Your chess experience just levelled up for free.
              </p>
            </div>

          </div>
        </div>
        <div style={{
          display: 'flex', flexDirection: 'column', justifyContent: 'center', alignItems: 'center', border: '1px solid #ff0050', backgroundColor: 'rgba(255, 0, 80, 0.1)',
          borderRadius: '8px',
        }}>
          <p style={{
            fontSize: '1.3rem',
            lineHeight: '1.6',
            fontStyle: 'italic',
            textAlign: 'center',
            maxWidth: '1000px',
            margin: '0 auto',
            padding: '30px',
            fontWeight: 'bold',


          }}>
            We do offer a premium version, primarily to reduce chess-related advertising,
            <br /><span style={{ color: 'gray' }}>(which you won't be bombarded with anyways).
            </span><br /><br />

            Premium users also wear a crown beside their <span style={{ color: 'gold', fontWeight: 'bold' }}>@Username</span><img src={crownGold} style={{ width: '30px', height: '30px' }} />,<br />get priority support, and access to advanced tools to support their growth.
          </p>
          <button onClick={() => window.open('/premium', '_blank')} style={{ border: '5px solid black', backgroundColor: 'gold', color: 'black', padding: '10px 20px', borderRadius: '5px', fontSize: '1.2rem', fontWeight: 'bold', cursor: 'pointer', marginBottom: '20px' }}>View Premium Plans</button>
        </div>
      </div> */}

      {/* <div style={{
        fontSize: '2.4rem',
        color: 'black',
        marginBottom: '30px',
        lineHeight: '.3',
        fontWeight: 'bold',
        textAlign: 'center',
        marginTop: '50px',
        textShadow: '0 0 5px gold'
      }}>
        Refreshing Chess. Dynamic Interactivity. Infinite Possibilities.
      </div>
      <div style={{
        fontSize: '3.3rem',
        color: 'black',
        lineHeight: '1.3',
        fontWeight: 'bold',
        textAlign: 'center',
        textShadow: '0 0 5px gold'
      }}>
        A True Social Media Platform, Built For Chess.
      </div> */}


      {/* <div style={{

        color: 'white',
        fontSize: '30px',
        fontStyle: 'italic',
        marginTop: '1100px',
        marginBottom: '30px',
        textAlign: 'center',
        textShadow: '0 0 10px black',
        width: '90%'
      }}
      >The Simplest Way To Share Chess.<br />Even Your Grandparents Could Join In On The Fun.</div>
      <img
        src={chessslogo}
        alt="ChessClip Logo"
        style={{ width: '200px', marginBottom: '' }}
      />
      <div style={{
        fontFamily: 'Mansalva, cursive',
        color: 'gold',
        fontSize: '5rem',
        marginBottom: '0px',
      }}>The Chess Republic</div> */}

      

    
    </div>
    ) : (
      <div className="" style={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        background: "linear-gradient(to top, #3066BE, black)",
        width: '100vw',
        height: '100vh',
        margin: 0,
        padding: '10px 0px',
        overflow: 'auto',
        scrollbarWidth: 'thin',
        scrollbarColor: 'gold transparent',
  
      }}>
  
  
       
  
        <style>
          {`
              @font-face {
                font-family: 'Mansalva';
                src: url(${mansalva}) format('truetype');
                font-weight: normal;
                font-style: normal;
              }
  
              @keyframes slideInFromRight {
                0% { 
                  transform: scale(0.5) translateX(200px);
                }
                100% { 
                  transform: scale(1) translateX(0);
                }
              }
  
              @keyframes slideInFromLeft {
                0% { 
                  transform: scale(0.5) translateX(-200px);
                }
                100% { 
                  transform: scale(1) translateX(0);
                }
              }
  
              .active-game-summary {
                animation: none;
              }
  
              .active-game-summary.slide-left {
                animation: slideInFromRight 0.5s cubic-bezier(0.4, 0, 0.2, 1) forwards;
              }
  
              .active-game-summary.slide-right {
                animation: slideInFromLeft 0.5s cubic-bezier(0.4, 0, 0.2, 1) forwards;
              }

              @keyframes slideUp {
                0% {
                  transform: translateY(100%);
                  opacity: 0;
                }
                20%, 80% {
                  transform: translateY(0);
                  opacity: 1;
                }
                100% {
                  transform: translateY(-100%);
                  opacity: 0;
                }
              }
              
              .rotating-word {
                animation: slideUp 2s ease-in-out infinite;
              }
            `}
        </style>
        <div>
         {/* <div style={{
          width: '100%',
          textAlign: 'center',
          marginBottom: '40px',
          marginTop: '20px'
        }}> */}
          {/* <div style={{
            maxWidth: '600px',
            margin: '0 auto',
            padding: '10px',
            backgroundColor: 'rgba(0,0,0,0.3)',
            borderRadius: '8px',
            border: '2px solid gold',
            boxShadow: '0 0 20px rgba(0,0,0,0.5)'
          }}>
            <h2 style={{
              color: 'gold',
              fontSize: '2rem',
              marginBottom: '20px',
              textShadow: '0 0 5px black'
            }}>
              Join The Waitlist
            </h2>
           
            <form onSubmit={handleEmailSubmit} style={{
              display: 'flex',
              flexDirection: 'column',
              gap: '15px',
              alignItems: 'center'
            }}>
              <input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="Enter your email"
                style={{
                  width: '300px',

                  padding: '15px',
                  fontSize: '1.2rem',
                  borderRadius: '5px',
                  border: '2px solid gold',
                  backgroundColor: 'rgba(255,255,255,0.1)',
                  color: 'white',
                  outline: 'none'
                }}
                disabled={isSubmitting}
              />
              <button
                type="submit"
                disabled={isSubmitting}
                style={{
                  padding: '15px 0px',
                  fontSize: '1.2rem',
                  fontWeight: 'bold',
                  backgroundColor: 'gold',
                  marginBottom: '10px',
                  color: 'black',
                  border: 'none',
                  borderRadius: '5px',
                  cursor: isSubmitting ? 'not-allowed' : 'pointer',
                  opacity: isSubmitting ? 0.7 : 1,
                  transition: 'all 0.2s ease',
                  width: '335px'
                }}
              >
                {isSubmitting && cooldownTimer > 0 
                  ? `Please wait ${cooldownTimer}s...` 
                  : 'Submit'}
              </button>
              {submitStatus === 'error' && (
                <p style={{ color: '#ff4444', marginTop: '10px' }}>
                  {errorMessage}
                </p>
              )}
              {submitStatus === 'success' && (
                <p style={{ color: '#4CAF50', marginTop: '10px' }}>
                  Thank you! We'll be in touch soon.
                </p>
              )}
              {isSubmitting && cooldownTimer > 0 && (
                <p style={{ color: 'gold', marginTop: '10px', fontSize: '0.9rem' }}>
                  Form will be available in {cooldownTimer} seconds
                </p>
              )}
            </form>
          </div> */}
        {/* </div> */}
          {/* <div className="finally" style={{ position: 'relative', top: '75px', left: '-70px', display: 'inline-block', fontSize: '2rem', transform: 'rotate(-15deg)', textShadow: 'none', color: 'gold', textShadow: '0 0 5px black', fontStyle: 'italic', fontFamily: 'Mansalva' }}>Finally...</div> */}
        {/* Two Column Layout */}
        <div style={{
          display: 'flex',
          maxWidth: '1200px',
          width: '100%',
          gap: '60px',
          alignItems: 'flex-start',
          marginTop: '5px',
          marginBottom: '30px'
        }}>
          
          {/* Left Column */}
          <div style={{
            flex: 1,
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            gap: '25px',
            paddingTop: '50px'
          }}>
             {/* Logo and Title Row */}
        <div style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          gap: '20px',
          marginTop: '25px',
          marginBottom: '25px'
        }}>
          <img
            src={chessslogo}
            alt="ChessClip Logo"
            style={{ width: '75px' }}
          />
          <div style={{
            fontFamily: 'Mansalva, cursive',
            color: 'gold',
            fontSize: '4rem'
          }}>ChessThread.com</div>
        </div>
          
            
            {/* Button Container - Two Equal Rows */}
            <div style={{
              width: '100%',
              maxWidth: '500px',
              display: 'flex',
              flexDirection: 'column',
              gap: '20px'
            }}>
             
              
              {/* Row 2: Signup and Login */}
              <div style={{
                display: 'flex',
                gap: '15px',
                width: '100%'
              }}>
                <Link to="/signup" style={{ flex: 1, textDecoration: 'none' }}>
                  <button style={{
                    width: '100%',
                    height: '80px',
                    fontSize: '1.4rem',
                    cursor: 'pointer',
                    borderRadius: '8px',
                    fontWeight: '600',
                    transition: 'all 0.2s ease',
                    border: '3px solid black',
                    backgroundColor: '#3066be',
                    color: 'white',
                    boxShadow: '0 4px 8px rgba(0,0,0,0.2)'
                  }}>
                    Sign-Up Free
                  </button>
                </Link>
                <Link to="/login" style={{ flex: 1, textDecoration: 'none' }}>
                  <button style={{
                    width: '100%',
                    height: '80px',
                    fontSize: '1.4rem',
                    cursor: 'pointer',
                    borderRadius: '8px',
                    fontWeight: '600',
                    transition: 'all 0.2s ease',
                    border: '3px solid black',
                    backgroundColor: 'gold',
                    color: 'black',
                    boxShadow: '0 4px 8px rgba(0,0,0,0.2)'
                  }}>
                    Login on Desktop
                  </button>
                </Link>
              </div>
               {/* Row 1: Google Play and App Store */}
               <div style={{
                display: 'flex',
                gap: '5px',
                width: '100%'
              }}>
                {/* Google Play Button */}
                {!googlePlayClicked ? (
                  <div
                    onClick={() => setGooglePlayClicked(true)}
                    style={{
                      flex: 1,
                      cursor: 'pointer'
                    }}
                  >
                    <img 
                      src={googlePlayImage} 
                      alt="Get it on Google Play"
                      style={{
                        width: '250px',
                        height: '80px',
                        objectFit: 'cover',
                        objectPosition: 'center',
                        borderRadius: '8px',
                        transition: 'transform 0.2s ease'
                      }}
                      onMouseOver={(e) => e.target.style.transform = 'scale(1.05)'}
                      onMouseOut={(e) => e.target.style.transform = 'scale(1)'}
                    />
                  </div>
                ) : (
                  <div
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      flex: 1,
                      height: '80px',
                      backgroundColor: '#000000',
                      color: 'white',
                      fontSize: '14px',
                      fontWeight: 'bold',
                      borderRadius: '8px',
                      border: '1px solid #333'
                    }}
                  >
                    Coming Soon to Google Play
                  </div>
                )}
                
                {/* App Store Button */}
                <a
                  href={APP_STORE_URL}
                  target="_blank"
                  rel="noopener noreferrer"
                  style={{
                    flex: 1,
                    textDecoration: 'none'
                  }}
                >
                  <img 
                    src={appStoreImage} 
                    alt="Download on the App Store"
                    style={{
                      width: '250px',
                      height: '80px',
                      objectFit: 'cover',
                      objectPosition: 'center',
                      borderRadius: '8px',
                      transition: 'transform 0.2s ease',
                      cursor: 'pointer'
                    }}
                    onMouseOver={(e) => e.target.style.transform = 'scale(1.05)'}
                    onMouseOut={(e) => e.target.style.transform = 'scale(1)'}
                  />
                </a>
              </div>
                  {/* For Chess [Rotating Word] Text */}
                    {/* Create Chess Content Text */}
            <div style={{
              fontSize: '2.6rem',
              color: 'white',
              lineHeight: '1.2',
              textShadow: '0 0 2px gold',
              textAlign: 'center',
              fontWeight: 'bold',
              paddingTop: '50px',
              fontFamily: 'system-ui, -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
              letterSpacing: '0.5px',
              lineHeight: '1.3',
              fontStyle: 'italic'

            }}>
              Create Chess Content
            </div>
        <div style={{
          fontSize: '2rem',
          color: '#ffffff',
          margin: '0',
          fontWeight: 'bold',
          fontFamily: 'Arial, sans-serif',
          letterSpacing: '1px',
          height: '30px',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          width: '100%',
          marginBottom: '',
          fontFamily: 'system-ui, -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
          letterSpacing: '0.5px',
          lineHeight: '1.3',
          fontStyle: 'italic'

        }}>
          For Chess <span 
            key={currentWordIndex}
            className="rotating-word"
            style={{ marginLeft: '8px' }}
          >
            {rotatingWords[currentWordIndex]}
          </span>
        </div>
            </div>
          </div>

          {/* Right Column - Screenshot */}
          <div style={{
            flex: 1,
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center'
          }}>
            <div style={{
              width: '400px',
              maxWidth: '100%'
            }}>
              <img
                src={mockups[currentMockupIndex]}
                alt="ChessThread App Screenshot"
                style={{
                  width: '100%',
                  height: 'auto',
                  transition: 'opacity 0.5s ease-in-out'
                }}
              />
            </div>
          </div>
        </div>

    
        </div>
  
  
  
  
       
        {/* Tabbed Content Section */}
        {/* <div style={{
          maxWidth: '1200px',
          margin: '30px 0',
          height: '700px', 
          display: 'flex',
          flexDirection: 'column'
        }}>
          <div style={{
            display: 'flex',
            justifyContent: 'space-around',
            borderBottom: '3px solid gold',
            marginBottom: '15px'
          }}>
            {['for Learning', 'for Creating', 'for Coaching'].map(tab => (
              <button
                key={tab}
                onClick={() => {
                  // When changing tabs, update the activeTab state
                  setActiveTab(tab);
                }}
                style={{
                  padding: '10px 20px',
                  fontSize: '1.5rem',
                  fontWeight: 'bold',
                  background: activeTab === tab ? 'gold' : 'transparent',
                  color: activeTab === tab ? 'black' : 'gold',
                  border: 'none',
                  borderRadius: '8px 8px 0 0',
                  cursor: 'pointer',
                  textTransform: 'capitalize'
                }}
              >
                {tab}
              </button>
            ))}
          </div>
  
          <div style={{
            padding: '15px',
            backgroundColor: 'rgba(0,0,0,0.3)',
            borderRadius: '0 0 8px 8px',
            flex: 1,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            overflow: 'visible' 
          }}>
            {typeof tabContent[activeTab] === 'string' ? (
              <p style={{
                fontSize: '1.4rem',
                lineHeight: '1.6',
                color: '#E0E0E0',
                textAlign: 'center'
              }}>
                {tabContent[activeTab]}
              </p>
            ) : (
              tabContent[activeTab]
            )}
          </div>
        </div> */}
  
        {/* <div style={{
          display: 'flex',
          gap: '50px',
          justifyContent: 'center',
          flexWrap: 'wrap',
          marginTop: '50px',
          marginBottom: '0px'
        }}>
          <Link to="/signup">
            <button style={{
              padding: '15px 30px',
              fontSize: '2.5rem',
              cursor: 'pointer',
              borderRadius: '8px',
              fontWeight: '600',
              transition: 'all 0.2s ease',
              border: '10px solid black',
              backgroundColor: '#3066be',
              color: 'black',
              height: '150px',
              width: '575px',
              boxShadow: '0 0 30px 0 gold'
            }}>
              Sign-Up Free
            </button>
          </Link>
          <Link to="/login">
            <button style={{
              padding: '15px 30px',
              fontSize: '2.5rem',
              cursor: 'pointer',
              borderRadius: '8px',
              fontWeight: '600',
              transition: 'all 0.2s ease',
              border: '10px solid black',
              backgroundColor: '#ff0050',
              color: 'black',
              height: '150px',
              width: '575px',
              boxShadow: '0 0 30px 0 gold'
  
            }}>
              Login
            </button>
          </Link>
        </div> */}
  
        {/* Free Features Section */}
        {/* <div style={{
          maxWidth: '1135px',
          margin: '50px 0 30px',
          padding: '30px',
          backgroundColor: 'rgba(0,0,0,0.3)',
          borderRadius: '8px',
          border: '2px solid gold',
          color: 'white',
          boxShadow: '0 0 30px rgba(0,0,0,0.5)'
        }}>
          <div style={{
            fontSize: '3rem',
            color: 'gold',
            marginBottom: '15px',
            textAlign: 'center',
            fontWeight: 'bold'
          }}>
            Most of Our Features Are <span style={{ fontStyle: 'italic' }}>*Literally*</span> Free
          </div>
  
          <p style={{
            fontSize: '1.3rem',
            lineHeight: '1.6',
            marginBottom: '25px',
            textAlign: 'center',
            fontWeight: 'bold'
          }}>
            That's right - we give you free access to tools and experiences that other websites charge you for. <br /> Here's what you get, no strings attached:
          </p>
  
          <div style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(500px, 1fr))',
            gap: '30px',
            marginBottom: '30px'
          }}>
            <div style={{
              display: 'flex',
              flexDirection: 'column',
              gap: '15px'
            }}>
              <div style={{
                padding: '20px',
                backgroundColor: 'rgba(48, 102, 190, 0.3)',
                borderRadius: '8px',
                border: '1px solid #3066BE'
              }}>
                <h3 style={{
                  fontSize: '1.6rem',
                  color: 'gold',
                  marginBottom: '10px'
                }}>
                  Elite Stockfish Game Review Analysis
                </h3>
                <p style={{ fontSize: '1.1rem', lineHeight: '1.5' }}>
                  Unlock unlimited reviews powered by the Stockfish engine—spot brilliancies, pinpoint mistakes, and elevate your chess skills, all at no cost.
  
                  <img src={Blunder} style={{ width: '25px', height: '25px', marginLeft: '10px' }} />
                  <img src={Theory} style={{ width: '25px', height: '25px', marginLeft: '10px' }} />
                  <img src={Brilliant} style={{ width: '25px', height: '25px', marginLeft: '10px' }} />
  
                </p>
              </div>
  
              <div style={{
                padding: '20px',
                backgroundColor: 'rgba(48, 102, 190, 0.3)',
                borderRadius: '8px',
                border: '1px solid #3066BE'
              }}>
                <h3 style={{
                  fontSize: '1.6rem',
                  color: 'gold',
                  marginBottom: '10px'
                }}>
                  PvP Chess Against Millions
                </h3>
                <p style={{ fontSize: '1.1rem', lineHeight: '1.5' }}>
                  Challenge players worldwide through Lichess.org with unlimited matches—top-tier matchmaking.
                </p>
              </div>
            </div>
  
            <div style={{
              display: 'flex',
              flexDirection: 'column',
              gap: '15px'
            }}>
              <div style={{
                padding: '20px',
                backgroundColor: 'rgba(48, 102, 190, 0.3)',
                borderRadius: '8px',
                border: '1px solid #3066BE'
              }}>
                <h3 style={{
                  fontSize: '1.6rem',
                  color: 'gold',
                  marginBottom: '10px'
                }}>
                  Endless Puzzles & Lessons
                </h3>
                <p style={{ fontSize: '1.1rem', lineHeight: '1.5' }}>
                  Dive into unlimited community-created puzzles, lessons, and content to sharpen your game—free access, no paywalls.
                </p>
              </div>
  
  
              <div style={{
                padding: '20px',
                backgroundColor: 'rgba(48, 102, 190, 0.3)',
                borderRadius: '8px',
                border: '1px solid #3066BE'
              }}>
                <h3 style={{
                  fontSize: '1.6rem',
                  color: 'gold',
                  marginBottom: '10px'
                }}>
                  More on the Way
                </h3>
                <p style={{ fontSize: '1.1rem', lineHeight: '1.5' }}>
                  Stay tuned for even greater features coming soon. Your chess experience just levelled up for free.
                </p>
              </div>
  
            </div>
          </div>
          <div style={{
            display: 'flex', flexDirection: 'column', justifyContent: 'center', alignItems: 'center', border: '1px solid #ff0050', backgroundColor: 'rgba(255, 0, 80, 0.1)',
            borderRadius: '8px',
          }}>
            <p style={{
              fontSize: '1.3rem',
              lineHeight: '1.6',
              fontStyle: 'italic',
              textAlign: 'center',
              maxWidth: '1000px',
              margin: '0 auto',
              padding: '30px',
              fontWeight: 'bold',
  
  
            }}>
              We do offer a premium version, primarily to reduce chess-related advertising,
              <br /><span style={{ color: 'gray' }}>(which you won't be bombarded with anyways).
              </span><br /><br />
  
              Premium users also wear a crown beside their <span style={{ color: 'gold', fontWeight: 'bold' }}>@Username</span><img src={crownGold} style={{ width: '30px', height: '30px' }} />,<br />get priority support, and access to advanced tools to support their growth.
            </p>
            <button onClick={() => window.open('/premium', '_blank')} style={{ border: '5px solid black', backgroundColor: 'gold', color: 'black', padding: '10px 20px', borderRadius: '5px', fontSize: '1.2rem', fontWeight: 'bold', cursor: 'pointer', marginBottom: '20px' }}>View Premium Plans</button>
          </div>
        </div> */}
  
        {/* <div style={{
          fontSize: '2.4rem',
          color: 'black',
          marginBottom: '30px',
          lineHeight: '.3',
          fontWeight: 'bold',
          textAlign: 'center',
          marginBottom: '20px',
          marginTop: '30px',
          textShadow: '0 0 5px gold'
        }}>
          Refreshing Chess. Dynamic Interactivity. Infinite Possibilities.
        </div>
        <div style={{
          fontSize: '3.3rem',
          color: 'black',
          lineHeight: '1.3',
          fontWeight: 'bold',
          textAlign: 'center',
          textShadow: '0 0 5px gold'
        }}>
          A True Social Media Platform, Built For Chess.
        </div> */}
  
  
        
 {/* <div style={{
  
          color: 'gold',
          fontSize: '40px',
          fontStyle: 'italic',
          marginTop: '30px',
          marginBottom: '30px',
          textAlign: 'center',
          textShadow: '0 0 10px black'
        }}
        >The Simplest Way To Share Chess.<br />Even Your Grandparents Could Join In On The Fun.</div>

        <img
          src={chessslogo}
          alt="ChessClip Logo"
          style={{ width: '200px', marginBottom: '' }}
        />
        <div style={{
          fontFamily: 'Mansalva, cursive',
          color: 'gold',
          fontSize: '5rem',
          marginBottom: '0px',
        }}>The Chess Republic</div> */}
  
        {/* <div style={{
          display: 'flex',
          gap: '50px',
          justifyContent: 'center',
          flexWrap: 'wrap',
          marginTop: '40px',
          marginBottom: '10px'
        }}>
          <Link to="/signup">
            <button style={{
              padding: '15px 30px',
              fontSize: '2.5rem',
              cursor: 'pointer',
              borderRadius: '8px',
              fontWeight: '600',
              transition: 'all 0.2s ease',
              border: '10px solid black',
              backgroundColor: '#3066be',
              color: 'black',
              height: '150px',
              width: '450px',
              boxShadow: '0 0 30px 0 gold'
            }}>
              Sign-Up Free
            </button>
          </Link>
          <Link to="/login">
            <button style={{
              padding: '15px 30px',
              fontSize: '2.5rem',
              cursor: 'pointer',
              borderRadius: '8px',
              fontWeight: '600',
              transition: 'all 0.2s ease',
              border: '10px solid black',
              backgroundColor: '#ff0050',
              color: 'black',
              height: '150px',
              width: '450px',
              boxShadow: '0 0 30px 0 gold'
  
            }}>
              Login
            </button>
          </Link>
        </div> */}
  
        {/* Rotating FEN Chessboard at bottom of page */}
        {/* <div style={{
          width: '100%',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          marginTop: '70px',
          marginBottom: '30px',
  
        }}>
  
          <RotatingFenBoard 
            initialFen={sampleFens[0]} 
            fenList={sampleFens} 
          />
  
        </div> */}
      </div>
    )}
  </div>
    
  );
};

export default Landing;


